import { ValidateOtpResponseDto } from "../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { CallActionRequest, CreateMeetingRoomRequest, CreateMeetingRoomResponse, InitiateCallRequest, InitiateCallResponse, JanusHangupRequest, MeetingRoomDto } from "../../interfaces/videocallDtos/videoCall";
import { Conversation } from "../chatService/ChatService";

export interface Publisher {
    feedId: string;
    display: string;
    userId?: string;
    isMuted?: boolean;
    isCameraOn?: boolean;
  }
  
  export interface SessionResponse {
    sessionId: number;
    handleId: number;
    sdpAnswer: string;
    feedId: string;
    privateId: number;
    // NEW: Includes the list of participants already in the room
    // to prevent a race condition on the client's initial sync.
    otherPublishers?: Publisher[];
  }

// export interface TrickleRequest {
//     sessionId: number;
//     handleId: number;
//     candidate: RTCIceCandidate;
// }

export interface TrickleCandidateDto {
    candidate: string;
    sdpMid?: string | null;
    sdpMLineIndex?: number | null;
  }
  
  export interface TrickleRequest {
    sessionId: number;
    handleId: number;
  
    // One of the two is present:
    candidate?: TrickleCandidateDto; // normal trickle
    completed?: boolean;             // end-of-candidates
  }
  

export interface HangupRequest {
    sessionId: number;
    handleId: number;
    roomId: string;
}

export interface SubscribeRequest {
    sessionId: number;
    handleId?: number;
    roomId: string | undefined;
    feedId: string;
    sdpOffer?: string;
}

export interface SubscribeResponse {
    subscriberHandleId?: number;
    receivedOffer?: string;
    needsAnswer?: boolean;
    sdpAnswer?: string;
}

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/video`;
type HeaderOptions = {
    authToken?: string;
    videoToken?: string;
    externalTicketId?: string;
    externalUserId?: string;
};

export const GetHeaders = (authToken: string, isJson: boolean = true): Record<string, string> => {
      const headers: Record<string, string> = {};
    if (isJson) {
        headers["Content-Type"] = "application/json";
    }
    const ex_details = localStorage.getItem("ExternalUserDetails");
    if (ex_details && (!authToken || authToken == "")) {
        const externalUserDetails: ValidateOtpResponseDto = JSON.parse(ex_details);
        headers["X-Video-Token"] = externalUserDetails.tempSessionToken;
        headers["X-External-TicketId"] = externalUserDetails.ticketId;
        headers["X-External_UserId"] = externalUserDetails.externalUserDetailId;
    } else {
        headers["Authorization"] = `Bearer ${authToken}`;
    }

    return headers;
};


/**
 * Sends the SDP offer to the backend, receives SDP answer from Janus via backend
 */
export async function sendOffer(
    displayName: string,
    roomId: string | undefined,
    sdpOffer: string,
    authToken: string,
    isMuted: boolean,    
    isCameraOn: boolean, 
    sessionId?: number | null,
    userId?: string | null,
): Promise<SessionResponse | null> {
    try {
        const response = await fetch(`${BASE_URL}/offer`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify({ displayName, roomId, sdpOffer, sessionId, isMuted, isCameraOn, userId }), // Ensure DTO matches backend
        });

        if (!response.ok) {
            console.error(
                "[sendOffer] Backend error status:",
                response.status,
                await response.text()
            );
            return null;
        }
        const responseBodyText = await response.text();
        if (!responseBodyText) {
            console.error("[sendOffer] Backend response body is empty.");
            return null;
        }
        try {
            const data: SessionResponse = JSON.parse(responseBodyText);
            if (
                !data.sessionId ||
                !data.handleId ||
                !data.feedId ||
                !data.privateId ||
                !data.sdpAnswer
            ) {
                console.warn("[sendOffer] Received data is incomplete:", data);
                // Depending on strictness, you might return null or throw error
            }
            return data;
        } catch (e) {
            console.error(
                "[sendOffer] Error parsing JSON from backend:",
                e,
                "Response text:",
                responseBodyText
            );
            return null;
        }
    } catch (err) {
        console.error("[sendOffer] Network or other error:", err);
        return null;
    }
}

function normalizeIceCandidate(
    ice: RTCIceCandidate | RTCIceCandidateInit
  ): TrickleCandidateDto | null {
    // Browser might pass RTCIceCandidate or the init shape
    const cand = "candidate" in ice ? ice.candidate : (ice as any)?.candidate;
    if (!cand) return null;
  
    const sdpMid =
      "sdpMid" in ice ? (ice as any).sdpMid ?? null : (ice as any)?.sdpMid ?? null;
    const sdpMLineIndex =
      "sdpMLineIndex" in ice
        ? (ice as any).sdpMLineIndex ?? null
        : (ice as any)?.sdpMLineIndex ?? null;
  
    return {
      candidate: cand,
      sdpMid,
      sdpMLineIndex,
    };
  }
  

/**
 * Sends a single ICE candidate to the backend for Janus trickle forwarding.
 * Accepts either RTCIceCandidate or RTCIceCandidateInit.
 */
export async function sendTrickleCandidate(
    sessionId: number,
    handleId: number,
    ice: RTCIceCandidate | RTCIceCandidateInit,
    authToken: string
  ): Promise<void> {
    try {
      const candidate = normalizeIceCandidate(ice);
      if (!candidate) {
        console.warn("[sendTrickleCandidate] Ignored empty/null candidate");
        return;
      }
  
      const req: TrickleRequest = {
        sessionId,
        handleId,
        candidate,
      };
  
      await fetch(`${BASE_URL}/trickle`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify(req),
      });
    } catch (err) {
      console.error("[sendTrickleCandidate] Failed to send candidate:", err);
    }
  }

  /**
 * Tells Janus we're done trickling for this PC.
 */
export async function sendTrickleCompleted(
    sessionId: number,
    handleId: number,
    authToken: string
  ): Promise<void> {
    try {
      const req: TrickleRequest = {
        sessionId,
        handleId,
        completed: true,
      };
  
      await fetch(`${BASE_URL}/trickle`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify(req),
      });
    } catch (err) {
      console.error("[sendTrickleCompleted] Failed to send completed:", err);
    }
  }
  
  
// export async function hangup(
//   req: HangupRequest,
//   authToken: string
// ): Promise<void> {
//   // Add authToken
//   try {
//     await fetch(`${BASE_URL}/hangup`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${authToken}`, // <<< ADD THIS
//       },
//       body: JSON.stringify(req),
//     });
//     console.log("[hangup] Hangup request sent.");
//   } catch (err) {
//     console.error("[hangup] Failed to send hangup request:", err);
//   }
// }

export async function subscribeToFeed(
    authToken: string,
    request: SubscribeRequest
): Promise<SubscribeResponse | null> {
    try {
        const response = await fetch(`${BASE_URL}/subscribe-feed`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            console.error(`[subscribeToFeed] Backend error. FeedId: ${request.feedId} | SessionId: ${request.sessionId}`, await response.text());
            return null;
        }
        const data: SubscribeResponse = await response.json();
        return data;
    } catch (err) {
        console.error(`[subscribeToFeed] Failed to subscribe to remote feed. FeedId: ${request.feedId} | SessionId: ${request.sessionId}`, err);
        return null;
    }
}

export async function getPublishers(
    authToken: string,
    roomId: string | undefined
): Promise<Publisher[]> {
    try {
        const response = await fetch(`${BASE_URL}/room/${roomId}/publishers`, {
            method: "GET",
            headers: GetHeaders(authToken),
        });

        if (!response.ok) {
            console.error("Failed to fetch publishers:", await response.text());
            return [];
        }

        const data: Publisher[] = await response.json(); // Correctly type the response
        return data;
    } catch (err) {
        console.error("[getPublishers] Network error:", err);
        return [];
    }
}

export async function hangup(sessionId: number, handleId: number, roomId: string, token: string, isHangupScreen :boolean = false,conversationId? : number, isCallRejectedHangUp : boolean = false) {
    console.log("[HangUp Request]", { sessionId, handleId, token });
    const payload :JanusHangupRequest = {
        sessionId: sessionId,
        handleId: handleId,
        roomId: roomId,
        isHangupScreen: isHangupScreen,
        conversationId: conversationId,
        isCallRejectedHangUp: isCallRejectedHangUp
    }
    const res = await fetch(`${BASE_URL}/hangup`, {
        method: "POST",
        headers: GetHeaders(token),
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to hang up session: ${err}`);
    }

    return res.json();
}

export async function hangupScreen(sessionId: number, handleId: number, roomId: string, token: string, isHangupScreen :boolean = false) : Promise<any> {
    console.log("[HangUp Request]", { sessionId, handleId, token });
    const payload :JanusHangupRequest = {
        sessionId: sessionId,
        handleId: handleId,
        roomId: roomId,
        isHangupScreen: isHangupScreen,
        isCallRejectedHangUp: false
    }
    const res = await fetch(`${BASE_URL}/hangup`, {
        method: "POST",
        headers: GetHeaders(token),
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to hang up session: ${err}`);
    }
    return true;
}

export async function getTurnCredentials(authToken: string): Promise<RTCIceServer[] | null> {
    try {
        const hea = GetHeaders(authToken);
        console.log("Head : ", hea);

        const response = await fetch(`${BASE_URL}/turn-credentials`, {
            method: "POST", // Matches the [HttpPost] attribute in the C# controller
            headers: hea,
        });

        if (!response.ok) {
            console.error("Failed to fetch TURN credentials from backend:", await response.text());
            return null;
        }

        // The backend returns the array of RTCIceServer objects directly
        const iceServers: RTCIceServer[] = await response.json();
        return iceServers;

    } catch (err) {
        console.error("[getTurnCredentials] Network error:", err);
        return null;
    }
}

/**
 * Sends a new SDP offer to an existing publisher handle for renegotiation (ICE Restart).
 * Backend returns the JSEP answer from Janus.
 */
export async function configurePublisher(
    sessionId: number,
    handleId: number,
    sdpOffer: string,
    authToken: string
  ): Promise<{ sdpAnswer: string } | null> {
    try {
      const response = await fetch(`${BASE_URL}/publisher/renegotiate`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify({ sessionId, handleId, sdpOffer }),
      });
  
      if (!response.ok) {
        console.error("[configurePublisher] Backend error:", await response.text());
        return null;
      }
  
      return await response.json();
    } catch (err) {
      console.error("[configurePublisher] Network error:", err);
      return null;
    }
  }
  

/**
 * Sends a keep-alive ping to the Janus session to prevent it from timing out.
 */
export async function keepAlive(sessionId: number, authToken: string): Promise<void> {
    try {
        await fetch(`${BASE_URL}/keepalive`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify({ sessionId }),
        });
    } catch (err) {
        // It's okay to silently fail here, the next one will likely succeed.
        console.warn("[keepAlive] Failed to send keep-alive ping:", err);
    }
}

export const createNewRoom = async (authToken: string, description: string): Promise<{ roomId: string }> => {
    const response = await fetch(`${BASE_URL}/rooms`, {
        method: 'POST',
        headers: GetHeaders(authToken),
        body: JSON.stringify({ description }),
    });
    if (!response.ok) {
        throw new Error('Failed to create a new room');
    }
    return response.json();
};


export const GetExistingOrCretateMeetingAsync = async (
    authToken: string,
    calendereventId: string | null = null,
    conversationId: string | null = null,
): Promise<ApiResponse<CreateMeetingRoomResponse>> => {
    try {
        const payload: CreateMeetingRoomRequest = {
            calendarEventId: calendereventId,
            conversationId: Number(conversationId)
        }
        const apiUrl = `${BASE_URL}/createmeetingroom`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        return await response.json();
    } catch (error) {
        throw error;
    }
}

export const InitiateVideoCallNotifyAsync = async (
    authToken: string,
    payload: InitiateCallRequest,
): Promise<InitiateCallResponse> => {
    try {

        const apiUrl = `${BASE_URL}/initiate`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        return await response.json();
    } catch (error) {
        throw error;
    }
}

export const GetMeetingByAssignedIdAsync = async (
    authToken: string,
    roomId: string,
    videoToken: string = "",
    externalTicketId: string = "",
    externalUserId: string = ""
): Promise<ApiResponse<MeetingRoomDto>> => {
    try {
        const apiUrl = `${BASE_URL}/getmeetingbyAssignedId/${roomId}`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "GET",
                headers: GetHeaders(authToken),
            }
        );
        const result = await response.json();
        return result;
    } catch (error) {
        throw error;
    }
}

// for saving Record Only
export const AcceptVideoCallAsync = async (
    authToken: string,
    payload: CallActionRequest,
): Promise<any | null> => {
    try {

        const apiUrl = `${BASE_URL}/accept`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );

        if (response.ok) {
            return true;
        }
        return false;
    } catch (error) {
        throw error;
    }
}

// for saving Record Only
export const RejectVideoCallAsync = async (
    authToken: string,
    payload: CallActionRequest,
): Promise<any | null> => {
    try {
        const apiUrl = `${BASE_URL}/reject`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        if (response.ok) {
            return true;
        }
        return false;
    } catch (error) {
        throw error;
    }
}

export const GetorCreateConversationForEventAsync = async (
    authToken: string,
    calendarEventId: string,
): Promise<ApiResponse<Conversation>> => {
    try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/getorcreate/conversationforEvent/${calendarEventId}`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "GET",
                headers: GetHeaders(authToken),
            }
        );

        return await response.json();
    } catch (error) {
        throw error;
    }
}

/** 
 * Gets a single-use hangup token from the backend for a specific session/handle.
 * This token is safe to use in a URL for navigator.sendBeacon.
 * @param sessionId The Janus session ID.
 * @param handleId The specific Janus handle ID to be hung up.
 * @param roomId The room ID associated with the session.
 * @param authToken The user's primary Firebase auth token.
 * @returns An object containing the hangupToken, or null if it fails.
 */
export async function getHangupToken(
    sessionId: number,
    handleId: number,
    roomId: string,
    authToken: string
  ): Promise<{ hangupToken: string } | null> {
    try {
      const response = await fetch(`${BASE_URL}/hangup-token`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify({ sessionId, handleId, roomId }),
      });
      if (!response.ok) {
          console.error("Failed to get hangup token, status:", response.status);
          return null;
      }
      return response.json();
    } catch (err) {
      console.error("[getHangupToken] Network error:", err);
      return null;
    }
  }

/**
 * Sends a hangup request using navigator.sendBeacon for reliable teardown on page unload.
 * NOTE: This requires a backend endpoint that can accept the auth token as a query parameter,
 * as sendBeacon cannot set Authorization headers.
 * @param {HangupRequest} req - The hangup request payload.
 * @param {string} token - The user's auth token.
 */
export function hangupWithBeacon(req: HangupRequest, token: string): void {
    // IMPORTANT: You need a dedicated backend endpoint for this.
    // Example: POST /video/hangup-beacon?authToken={token}
    const url = `${BASE_URL}/hangup-beacon?token=${encodeURIComponent(token)}`;
    const blob = new Blob([JSON.stringify(req)], { type: 'application/json; charset=UTF-8' });
  
    // Use sendBeacon for reliable "fire-and-forget" delivery
    if (!navigator.sendBeacon(url, blob)) {
      console.error("[hangupWithBeacon] Failed to queue beacon request.");
      // As a fallback for older browsers, you could potentially use a synchronous XHR,
      // but this is discouraged as it blocks the UI thread.
    } else {
      console.log("[hangupWithBeacon] Beacon hangup request queued for:", req);
    }
  }

export function getExternalUserDetails(): ValidateOtpResponseDto | null {
    const raw = localStorage.getItem("ExternalUserDetails");
    return raw ? JSON.parse(raw) as ValidateOtpResponseDto : null;
}